import api from '../store/api';

const ACCESS_TOKEN_KEY = 'orbital-access-token';

function shouldTrackRequest(config = {}) {
  return config.incrementRequestCount !== false;
}

function requestUsedOrbitalToken(config, accessToken) {
  if (!accessToken) return false;
  const authorization = config.headers && config.headers.Authorization;
  return !authorization || authorization === `Token ${accessToken}`;
}

function responseBelongsToCurrentSession(config = {}) {
  const requestToken = config.orbitalAccessToken;
  return !requestToken || requestToken === localStorage.getItem(ACCESS_TOKEN_KEY);
}

function responseErrors(data) {
  if (!data || typeof data !== 'object') return [];

  const errors = [];
  Object.keys(data).forEach((fieldName) => {
    if (['message', 'token', 'rawResponseData'].includes(fieldName)) return;
    const value = data[fieldName];
    if (value === null || value === undefined || value === '') return;

    errors.push({
      fieldName,
      message: Array.isArray(value) ? String(value[0]) : String(value),
    });
  });
  return errors;
}

export default function setup(app) {
  const requestInterceptor = api.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    config.orbitalAccessToken = accessToken;
    config.orbitalAuthenticatedRequest = requestUsedOrbitalToken(config, accessToken);

    if (shouldTrackRequest(config)) {
      app.$store.commit('UPDATE_PENDING_REQUEST_COUNT', 1, { root: true });
    }
    return config;
  });

  const responseInterceptor = api.interceptors.response.use((response) => {
    if (shouldTrackRequest(response.config)) {
      app.$store.commit('UPDATE_PENDING_REQUEST_COUNT', -1, { root: true });
    }

    if (!responseBelongsToCurrentSession(response.config)) {
      const error = new Error('Ignoring a response from an expired session.');
      error.config = response.config;
      error.isSessionExpired = true;
      return Promise.reject(error);
    }
    return response;
  }, async (error) => {
    const config = (error && error.config) || {};
    const response = error && error.response;

    if (shouldTrackRequest(config)) {
      app.$store.commit('UPDATE_PENDING_REQUEST_COUNT', -1, { root: true });
    }

    if (!responseBelongsToCurrentSession(config)) {
      error.isSessionExpired = true;
      return Promise.reject(error);
    }

    if (response && response.status === 401 && config.orbitalAuthenticatedRequest) {
      error.isSessionExpired = true;
      if (responseBelongsToCurrentSession(config)) {
        await app.$store.dispatch('expireSession');
      }
      return Promise.reject(error);
    }

    if (config.suppressGlobalErrors) return Promise.reject(error);

    const errors = responseErrors(response && response.data);
    if (errors.length) {
      app.$store.commit('UPDATE_GLOBAL_ERRORS', errors);
    } else if (!response || response.status !== 401) {
      app.$store.commit('UPDATE_GLOBAL_ERRORS', ['Your request failed. Try again later.']);
    }
    return Promise.reject(error);
  });

  return () => {
    api.interceptors.request.eject(requestInterceptor);
    api.interceptors.response.eject(responseInterceptor);
  };
}
